import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { CheckLoginResultData, IChangePasswordParams, ICheckLoginParams as ICheckLoginParams, ILoginCredentials, LoginResultData } from "./types";

/**
 * Rozhraní definující metody zajišťující komunikaci se službami zaopatřující proces přihlášení.
 * Tyto metody zajišťují volání AS.
 * @includeToDoc
 */
export interface IAuthClient {

    /**
     * Přesměruje na stránku určenou k přihlašování.
     * @param queryString Volitelné další parametry k předání do url pro přihlašování. Řetězec musí být platným url řetězcem.
     * @param clientCode Kód klienta, kterým je identifikován v rámci autentizace na webové službě.
     */
    redirectToLoginPage(clientCode: string, queryString?: string): void;

    /**
     * Volá přihlášení.
     * @param credentials Údaje potřebné k přihlášení
     * @param cancellationToken Token k přerušení operace
     *
     * @throws {TwoFARequiredException} Vyvolá při požadavku na dvoufazovou autentizaci
     */
    login(credentials: ILoginCredentials, cancellationToken?: CancellationToken): Promise<LoginResultData>;

    /**
     * Volá odhlášení.
     * @param clientCode Kód klienta, kterým je identifikován v rámci autentizace na webové službě.
     * @param cancellationToken Token k přerušení operace
     */
    logout(clientCode: string, cancellationToken?: CancellationToken): Promise<string>;

    /**
     * Volá změnu hesla.
     * @param params Údaje potřebné ke změne hesla
     * @param cancellationToken Token k přerušení operace
     */
    changeExpiredPassword(params: IChangePasswordParams, cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Přihlásí se jako anonymní uživatel.
     * @param language Jazyk pro přihlášení
     * @param cancellationToken Token k přerušení operace
     */
    loginAnonymously(language: string, cancellationToken?: CancellationToken): Promise<string>;

    /**
     * Uvěřuje stav přihlášení aktuálního uživatele.
     * @param params Parametry operace kontroly
     * @param cancellationToken Token k přerušení operace
     */
    checkLogin(params: ICheckLoginParams, cancellationToken?: CancellationToken): Promise<CheckLoginResultData>;
}

export class IAuthClient {
    private static _methods: Array<keyof IAuthClient> = [
        "redirectToLoginPage",
        "login",
        "logout",
        "loginAnonymously",
        "checkLogin",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}
